import isNil from 'lodash/isNil';
import { assert } from 'ts-essentials';
import { shallowOmit } from '../utils/object-utils';
import { record } from '../utils/recorder';
import { cloneDeep } from '../utils/clone-deep';
import { SetTextDataMutation, TextDataUtils } from '../mutation/text/set-text-data-mutation';
import {
    SetTextBodyPropertiesMutation,
    TextBodyPropertiesUtils,
} from '../mutation/text/set-text-body-properties-mutation';
import { clone, merge } from '../utils/protoutil';
import {
    tableMergedRanges,
    hasGridSpan,
    hasRowSpan,
    cellHasParagraphText,
    isValidTableRange,
} from '../utils/table-utils';
import { InsertDirection, DeleteDirection, ModifyDirection } from '../constants/table-constants';
import { DeleteTableColMutation } from '../mutation/shape/table/delete-table-col-mutation';
import { DeleteTableRowMutation } from '../mutation/shape/table/delete-table-row-mutation';
import { InsertTableColMutation } from '../mutation/shape/table/insert-table-col-mutation';
import { InsertTableRowMutation } from '../mutation/shape/table/insert-table-row-mutation';
import { MergeTableCellsMutation } from '../mutation/shape/table/merge-table-cells-mutation';
import { SetTableColWidthsMutation } from '../mutation/shape/table/set-table-col-width-mutation';
import { SetTableRowHeightsMutation } from '../mutation/shape/table/set-table-row-height-mutation';
import { UnMergeTableCellsMutation } from '../mutation/shape/table/unmerge-table-cells-mutation';
import { SetTableCellPropertiesMutation } from '../mutation/shape/table/set-table-cell-properties-mutation';
import { GraphicFrameBaseMutationAPI } from './graphic-frame-base-mutation-api';
import type { DeepReadonly } from 'ts-essentials';
import type { Mutation } from '../mutation/base/base-mutation';
import type { TableDataModel } from '../model/table-data-model';
import type { ITableData } from '../data';
import type { DeltaOf } from '../utils/delta';
import type {
    IHeightInfo,
    IWidthInfo,
    ITableRangeData,
    ICTTextParagraph,
    ICTTextBodyPropertiesDelta,
    ICTTextListStyleDelta,
    ICTTransform2D,
    ICTTableCellProperties,
    ICTTableCell,
    ICTTableCol,
    ICTTableRow,
    ICTTableDmlA,
} from '@tencent/slide-sdk-pb-model';

export interface ITableSizeOptions {
    xfrm?: ICTTransform2D;
    heights?: number[];
    widths?: number[];
    operation: ModifyDirection;
}

export class TableMutationAPI extends GraphicFrameBaseMutationAPI<ITableData, TableDataModel> {
    /**
     * 更新 GraphicalObjectFrame 属性
     */
    @record()
    public updateProps({ spPr, nvPr, graphic, bwMode }: DeltaOf<ITableData>) {
        if (!graphic?.graphicData?.tbl?.tblPr) {
            return super.updateProps({ spPr, nvPr, graphic, bwMode });
        }
        // tblPr 全量更新
        const tblPr = merge(
            cloneDeep(this.model.store.data.graphic?.graphicData?.tbl?.tblPr ?? {}),
            graphic.graphicData.tbl.tblPr,
        );
        return super.updateProps({
            spPr,
            nvPr,
            graphic: {
                ...graphic,
                graphicData: { ...graphic.graphicData, tbl: { ...graphic.graphicData.tbl, tblPr } },
            },
            bwMode,
        });
    }

    /**
     * 更新段落
     * @param paragraphList 段落列表
     */
    @record()
    public updateParagraphList(paragraphList: ICTTextParagraph[], col: number, row: number) {
        return this.transaction(() => {
            this.request([
                new SetTextDataMutation(
                    shallowOmit(
                        TextDataUtils.fromParagraphListData(
                            paragraphList,
                            this.model.getPage().id,
                            this.model.id,
                            col,
                            row,
                        ),
                        'type',
                    ),
                ),
            ]);
        });
    }

    @record()
    public updateTextBodyProperties({
        bodyPr,
        lstStyle,
        col,
        row,
    }: {
        bodyPr?: DeltaOf<ICTTextBodyPropertiesDelta>;
        lstStyle?: DeltaOf<ICTTextListStyleDelta>;
        col: number;
        row: number;
    }) {
        return this.transaction(() => {
            this.request([
                new SetTextBodyPropertiesMutation(
                    shallowOmit(
                        TextBodyPropertiesUtils.fromTextBodyProperties(
                            { bodyPr, lstStyle },
                            this.model.getPage().id,
                            this.model.id,
                            col,
                            row,
                        ),
                        'type',
                    ),
                ),
            ]);
        });
    }

    /**
     * 根据选中的范围执行插入行/列的操作
     * @param start 插入的起始点,
     * @param count 插入的行列数目,
     * @param operation 0:上方插入行，1:下方插入行，2:左侧插入列，3:右侧插入列
     */
    @record()
    public insertRowsOrColumns(start: number, count: number, operation: InsertDirection) {
        const operations: Record<InsertDirection, () => void> = {
            [InsertDirection.ABOVE]: () => this.insertTableRows(start, count, InsertDirection.ABOVE),
            [InsertDirection.BELOW]: () => this.insertTableRows(start, count, InsertDirection.BELOW),
            [InsertDirection.LEFT]: () => this.insertTableCols(start, count, InsertDirection.LEFT),
            [InsertDirection.RIGHT]: () => this.insertTableCols(start, count, InsertDirection.RIGHT),
        };
        operations[operation]?.();
    }

    /**
     * 根据选中的范围删除行或者列
     * @param range 选中范围的其实坐标，startRowIndex: 行起点，rowSpan: 选中行数, startColIndex: 列起点，colSpan: 选中列数,
     * @param operation 0: 删除行，1: 删除列
     */
    @record()
    public deleteRowsOrColumns(start: number, count: number, operation: DeleteDirection) {
        const operations: Record<DeleteDirection, () => void> = {
            [DeleteDirection.ROW]: () => this.deleteTableRows(start, count),
            [DeleteDirection.COLUMN]: () => this.deleteTableCols(start, count),
        };
        operations[operation]?.();
    }

    /**
     * 合并单元格
     */
    @record()
    public mergeCell(range: Required<ITableRangeData>) {
        const { startRowIndex, startColIndex, rowSpan, colSpan } = range;
        if (isNil(startRowIndex) || !rowSpan || isNil(startColIndex) || !colSpan) return;
        const canMerge = this.canMergeCell(range);
        if (!canMerge) return;

        const tableData = cloneDeep(this.model.getOwnedData().graphic?.graphicData?.tbl) as ICTTableDmlA;
        const rowList = tableData?.tr as ICTTableRow[];
        assert(rowList, 'table row list is null');
        let subTable = rowList.slice(startRowIndex, startRowIndex + rowSpan);
        subTable = subTable.map((row: ICTTableRow) => ({
            h: row.h,
            tc: (row?.tc && row.tc.slice(startColIndex, startColIndex + colSpan)) || [],
        }));

        this.transaction(() => {
            // 0. 获取范围内所有的已合并单元格，将他们解除合并
            const ranges = tableMergedRanges(tableData, range);
            this.forEachInTransaction(ranges, (range) => this.cancelMergedCellInternal(range));

            // 1. 合并单元格
            this.mergeCellInternal(range);

            // 2. 处理单元格文本
            const [firsetCell] = subTable[0].tc || [];
            const hasTextBody = firsetCell?.txBody?.p && cellHasParagraphText(firsetCell.txBody.p);
            const paragraphList = (hasTextBody && cloneDeep(firsetCell?.txBody?.p)) || [];

            this.forEachInTransaction(subTable as ICTTableRow[], (row, rowIndex) => {
                this.forEachInTransaction(row?.tc as ICTTableCell[], (cell, colIndex) => {
                    // 循环遍历，但是略过左上角第一个单元格
                    if (rowIndex === 0 && colIndex === 0) return;
                    // 清空其余单元格的文本，并将文本追加到 paragraphList 上
                    if (cell.txBody) {
                        const hasText = cell?.txBody?.p && cellHasParagraphText(cell.txBody.p);
                        // 如果该单元格有文本，将单元格文本清空
                        if (hasText) {
                            cell?.txBody?.p && paragraphList.push(...cell.txBody.p);
                            this.updateParagraphList(
                                [
                                    {
                                        egTextRun: [],
                                        endParaRPr: {},
                                        pPr: {},
                                    },
                                ],
                                startColIndex + colIndex,
                                startRowIndex + rowIndex,
                            );
                        }
                    }
                });
            });

            // 3. 更新左上角单元格文本
            this.updateParagraphList(paragraphList, startColIndex, startRowIndex);
        });
    }

    /**
     * 取消合并单元格
     */
    @record()
    public cancelMergedCell(range: Required<ITableRangeData>) {
        const { startRowIndex, startColIndex, rowSpan, colSpan } = range;
        if (isNil(startRowIndex) || !rowSpan || isNil(startColIndex) || !colSpan) return;
        const cloneTable = cloneDeep(this.model.getOwnedData().graphic?.graphicData?.tbl);
        const rowList = cloneTable?.tr as ICTTableRow[];
        const colList = cloneTable?.tblGrid?.gridCol;

        if (!rowList || !colList) return;
        if (startRowIndex + rowSpan > rowList.length || startColIndex + colSpan > colList.length) return;

        const mergedCells: ICTTableRow[] = rowList
            .slice(startRowIndex, startRowIndex + rowSpan)
            ?.map((row: ICTTableRow) => ({
                h: row.h,
                tc: (row?.tc && row.tc.slice(startColIndex, startColIndex + colSpan)) || [],
            }));

        this.transaction(() => {
            // 1. 取消合并单元格
            this.cancelMergedCellInternal(range);

            // 2. 设置合并的单元格文字为空
            this.forEachInTransaction(mergedCells as ICTTableRow[], (row, rowIndex) => {
                this.forEachInTransaction(row?.tc as ICTTableCell[], (cell, colIndex) => {
                    // 循环遍历，但是略过左上角第一个单元格
                    if (rowIndex === 0 && colIndex === 0) return;
                    this.updateParagraphList([], startColIndex + colIndex, startRowIndex + rowIndex);
                });
            });
        });
    }

    /**
     * 修改表格行高/列宽
     * @param options 更新选项。包括 xfrm: 位置信息，heights: 行高列表，widths: 列宽列表，operation: 操作选项
     * @param operation 0: 修改行高&列宽，1: 修改行高，2: 修改列宽
     */
    @record()
    public updateTableWidthOrHeight(options: ITableSizeOptions) {
        const { xfrm, heights, widths, operation } = options;

        const heightInfo: IHeightInfo[] =
            (heights && heights.length === this.model.rowLength && heights.map((h, i) => ({ index: i, height: h }))) ||
            [];
        const widthInfo: IWidthInfo[] =
            (widths && widths.length === this.model.colLength && widths.map((w, i) => ({ index: i, width: w }))) || [];

        this.transaction(() => {
            if (xfrm) {
                this.updateTransform(xfrm);
            }

            const mutations: Mutation[] = [];
            if ((operation === ModifyDirection.ALL || operation === ModifyDirection.HEIGHT) && heightInfo.length > 0) {
                mutations.push(
                    new SetTableRowHeightsMutation({
                        pageId: this.model.getPage().id,
                        shapeId: this.model.id,
                        heights: heightInfo,
                    }),
                );
            }
            if ((operation === ModifyDirection.ALL || operation === ModifyDirection.WIDTH) && widthInfo.length > 0) {
                mutations.push(
                    new SetTableColWidthsMutation({
                        pageId: this.model.getPage().id,
                        shapeId: this.model.id,
                        widths: widthInfo,
                    }),
                );
            }

            this.request(mutations);
        });
    }

    /**
     * 设置单元格行高
     */
    @record()
    public setTableRowHeights(heights: IHeightInfo[]) {
        this.transaction(() => {
            this.request([
                new SetTableRowHeightsMutation({
                    pageId: this.model.getPage().id,
                    shapeId: this.model.id,
                    heights,
                }),
            ]);
        });
    }

    /**
     * 设置单元格列宽
     */
    @record()
    public setTableColWidths(widths: IWidthInfo[]) {
        this.transaction(() => {
            this.request([
                new SetTableColWidthsMutation({
                    pageId: this.model.getPage().id,
                    shapeId: this.model.id,
                    widths,
                }),
            ]);
        });
    }

    /**
     * 更新某个范围内单元格属性
     */
    @record()
    public updateTableCellProperties(
        tcPr: DeepReadonly<DeltaOf<ICTTableCellProperties>>,
        range: Required<ITableRangeData>,
    ) {
        // FIXME: CellProperties 在中台全量替换，不可以使用 range 批量设置属性，只能对每个单元格依次设置属性。
        // 现在每个单元格都会有一个 mutation，之后可以优化一下这里
        this.model.store.iterateTableRange(range, (row, col, cell) => {
            this.transaction(() => {
                this.request([
                    new SetTableCellPropertiesMutation({
                        pageId: this.model.getPage().id,
                        shapeId: this.model.id,
                        range: {
                            startRowIndex: row,
                            startColIndex: col,
                            rowSpan: 1,
                            colSpan: 1,
                        },
                        tableCellProperties: tcPr && merge(cloneDeep(cell.tcPr ?? {}), tcPr),
                    }),
                ]);
            });
        });
    }

    private mergeCellInternal(range: Required<ITableRangeData>) {
        this.transaction(() => {
            this.request([
                new MergeTableCellsMutation({
                    pageId: this.model.getPage().id,
                    shapeId: this.model.id,
                    range,
                }),
            ]);
        });
    }

    private cancelMergedCellInternal(range: Required<ITableRangeData>) {
        this.transaction(() => {
            // 1. 取消合并单元格
            this.request([
                new UnMergeTableCellsMutation({
                    pageId: this.model.getPage().id,
                    shapeId: this.model.id,
                    range,
                }),
            ]);
        });
    }

    /**
     * 插入表格行
     */
    private insertTableRows(startRow: number, countRow: number, operation: InsertDirection) {
        function getCopyRowIndexBeforeInsert(start: number, count: number, total: number) {
            if (operation === InsertDirection.BELOW) {
                if (start === 0) {
                    return start;
                }
                return start - 1;
            }
            if (start === total) {
                return start - 1;
            }
            return start;
        }

        const tableData = cloneDeep(this.model.getOwnedData().graphic?.graphicData?.tbl);
        // 选中的行，新插入的行需要复制这一行的属性
        const selectRowIndex = getCopyRowIndexBeforeInsert(startRow, countRow, tableData?.tr?.length ?? 0);
        const selectRow = tableData?.tr?.[selectRowIndex];
        const selectTCList = selectRow?.tc;
        if (!tableData || !selectTCList) return;
        const mergedRanges = tableMergedRanges(tableData as ICTTableDmlA);

        this.transaction(() => {
            // 将范围内的单元格进行解组
            this.forEachInTransaction(mergedRanges, (range) => {
                if (startRow > range.startRowIndex && startRow < range.startRowIndex + range.rowSpan) {
                    this.cancelMergedCell({
                        startRowIndex: range.startRowIndex,
                        startColIndex: range.startColIndex,
                        rowSpan: range.rowSpan,
                        colSpan: range.colSpan,
                    });
                }
            });
            // 1. 插入行
            this.insertTableRowsInternal(startRow, countRow);
            // 2. 设置插入的行的行高
            const heights = Array.from({ length: countRow }, (_, index) => ({
                index: startRow + index,
                height: selectRow.h,
            }));
            this.setTableRowHeights(heights);
            // 3. 遍历单元格，更新属性
            const addRows = this.model
                .getOwnedData()
                .graphic?.graphicData?.tbl?.tr?.slice(startRow, startRow + countRow);
            this.forEachInTransaction(addRows as ICTTableRow[], (row, rowIndex) => {
                this.forEachInTransaction(selectTCList as ICTTableCell[], (selectedCell, colIndex) => {
                    // 设置单元格属性
                    if (selectedCell?.tcPr) {
                        this.updateTableCellProperties(selectedCell.tcPr, {
                            startRowIndex: startRow + rowIndex,
                            startColIndex: colIndex,
                            rowSpan: 1,
                            colSpan: 1,
                        });
                    }
                    // 设置单元格文本属性
                    if (selectedCell?.txBody?.bodyPr || selectedCell?.txBody?.lstStyle) {
                        this.updateTextBodyProperties({
                            bodyPr: selectedCell.txBody?.bodyPr ?? {},
                            lstStyle: selectedCell.txBody?.lstStyle ?? {},
                            col: colIndex,
                            row: startRow + rowIndex,
                        });
                    }
                    // 设置单元格文本
                    if (selectedCell.txBody?.p?.[0]) {
                        this.addCellTextBody(selectedCell?.txBody?.p?.[0], colIndex, startRow + rowIndex);
                    }
                    // 设置横向合并单元格
                    if (selectedCell.gridSpan) {
                        this.mergeCellInternal({
                            startRowIndex: startRow + rowIndex,
                            startColIndex: colIndex,
                            rowSpan: 1,
                            colSpan: selectedCell.gridSpan,
                        });
                    }
                });
            });
            // 4. 设置纵向合并单元格
            this.forEachInTransaction(mergedRanges, (range) => {
                if (startRow > range.startRowIndex && startRow < range.startRowIndex + range.rowSpan) {
                    this.mergeCell({
                        startRowIndex: range.startRowIndex,
                        startColIndex: range.startColIndex,
                        rowSpan: range.rowSpan + countRow,
                        colSpan: range.colSpan,
                    });
                }
            });
            // 5. 设置表格行高
            const totalHeight = this.model
                .getOwnedData()
                .graphic?.graphicData?.tbl?.tr?.reduce((sum, row) => sum + (row?.h || 0), 0);
            const xfrm = this.model.getOwnedData()?.spPr?.xfrm;
            this.updateTransform({
                ext: {
                    cx: xfrm?.ext?.cx,
                    cy: totalHeight,
                },
                ...xfrm,
            });
        });
    }

    private insertTableRowsInternal(insertPoint: number, countRow: number) {
        this.transaction(() => {
            this.request([
                new InsertTableRowMutation({
                    pageId: this.model.getPage().id,
                    shapeId: this.model.id,
                    index: insertPoint,
                    count: countRow,
                }),
            ]);
        });
    }

    /**
     * 插入表格列
     */
    private insertTableCols(startCol: number, countCol: number, operation: InsertDirection) {
        // 获取插入后，新插入的列应该继承哪一列的属性, 注意边界条件
        function getCopyColIndexAfterInsert(start: number, count: number, total: number) {
            if (operation === InsertDirection.RIGHT) {
                if (start === 0) {
                    return start + count;
                }
                return start - 1;
            }
            if (start === total) {
                return start - 1;
            }
            return start + count;
        }

        // 表格合并的范围
        const mergedRanges = tableMergedRanges(this.model.getOwnedData().graphic?.graphicData?.tbl as ICTTableDmlA);

        this.transaction(() => {
            // 将范围内的单元格进行解组
            this.forEachInTransaction(mergedRanges, (range) => {
                if (startCol > range.startColIndex && startCol < range.startColIndex + range.colSpan) {
                    this.cancelMergedCell({
                        startRowIndex: range.startRowIndex,
                        startColIndex: range.startColIndex,
                        rowSpan: range.rowSpan,
                        colSpan: range.colSpan,
                    });
                }
            });
            // 1. 插入列
            this.insertTableColsInternal(startCol, countCol);

            // 2. 总宽度不变，因此需要重新计算每列的宽度
            const colList = this.model.getOwnedData().graphic?.graphicData?.tbl?.tblGrid?.gridCol;
            // 选中的列，新插入的列需要复制这一列的属性
            const selectColIndex = getCopyColIndexAfterInsert(
                startCol,
                countCol,
                (colList?.length ?? countCol) - countCol,
            );
            if (!colList) return;
            const totalWidth =
                colList.reduce((sum, gridCol) => sum + (gridCol?.w || 0), 0) +
                (colList[selectColIndex]?.w || 0) * countCol;
            const scale = (this.model.getOwnedData()?.spPr?.xfrm?.ext?.cx || 0) / totalWidth ?? 1;
            const expectedColList = colList.map((grid, colIndex) => {
                const width =
                    colIndex >= startCol && colIndex < startCol + countCol ? colList[selectColIndex].w : grid.w;
                return {
                    index: colIndex,
                    width: (width ?? 1) * scale,
                };
            });
            this.setTableColWidths(expectedColList);

            // 3. 遍历单元格，更新属性
            const rowList = this.model.getOwnedData().graphic?.graphicData?.tbl?.tr;
            this.forEachInTransaction(rowList as ICTTableRow[], (row, rowIndex) => {
                // 选中的单元格
                const selectedCell = row?.tc?.[selectColIndex];
                const haveInsertCell = row.tc?.slice(startCol, startCol + countCol);
                this.forEachInTransaction(haveInsertCell as ICTTableCell[], (cell, cellIndex) => {
                    // 设置单元格属性
                    if (selectedCell?.tcPr) {
                        this.updateTableCellProperties(selectedCell.tcPr, {
                            startRowIndex: rowIndex,
                            startColIndex: startCol + cellIndex,
                            rowSpan: 1,
                            colSpan: 1,
                        });
                    }
                    // 设置单元格文本属性
                    if (selectedCell?.txBody?.bodyPr || selectedCell?.txBody?.lstStyle) {
                        this.updateTextBodyProperties({
                            bodyPr: selectedCell.txBody?.bodyPr ?? {},
                            lstStyle: selectedCell.txBody?.lstStyle ?? {},
                            col: startCol + cellIndex,
                            row: rowIndex,
                        });
                    }
                    // 设置单元格文本
                    if (selectedCell?.txBody?.p?.[0]) {
                        this.addCellTextBody(selectedCell?.txBody?.p?.[0], startCol + cellIndex, rowIndex);
                    }
                    // 设置纵向合并单元格
                    if (selectedCell?.rowSpan) {
                        this.mergeCell({
                            startRowIndex: rowIndex,
                            startColIndex: startCol + cellIndex,
                            rowSpan: selectedCell.rowSpan,
                            colSpan: 1,
                        });
                    }
                });
            });

            // 4. 设置横向合并单元格
            this.forEachInTransaction(mergedRanges, (range) => {
                if (startCol > range.startColIndex && startCol < range.startColIndex + range.colSpan) {
                    this.mergeCell({
                        startRowIndex: range.startRowIndex,
                        startColIndex: range.startColIndex,
                        rowSpan: range.rowSpan,
                        colSpan: range.colSpan + countCol,
                    });
                }
            });
        });
    }

    /**
     * 添加单元格更新文本
     */
    private addCellTextBody(paragraph: ICTTextParagraph, colIndex: number, rowIndex: number) {
        const pList = clone([paragraph]);
        // 继承文本样式
        if (pList[0].egTextRun && pList[0].egTextRun.length > 0) {
            const lastIndex = pList[0].egTextRun.length - 1;
            // 文本样式去掉链接
            const rPr = pList[0].egTextRun[lastIndex].r?.rPr;
            if (rPr) {
                pList[0].endParaRPr = shallowOmit(rPr, 'hlinkClick');
            }
        }
        pList[0].egTextRun = [];
        this.updateParagraphList(pList, colIndex, rowIndex);
    }

    private insertTableColsInternal(startCol: number, countCol: number) {
        this.transaction(() => {
            this.request([
                new InsertTableColMutation({
                    pageId: this.model.getPage().id,
                    shapeId: this.model.id,
                    index: startCol,
                    count: countCol,
                }),
            ]);
        });
    }

    /**
     * 删除表格行
     */
    private deleteTableRows(startRow: number, countRow: number) {
        const tableData = cloneDeep(this.model.getOwnedData().graphic?.graphicData?.tbl);
        if (!tableData) return;

        const deleteRows = tableData?.tr.slice(startRow, startRow + countRow) as ICTTableRow[];
        const mergedRanges = tableMergedRanges(tableData as ICTTableDmlA);

        const isOutOfDeleteRange = (range: Required<ITableRangeData>, startRow: number, countRow: number) => {
            if (!isValidTableRange(range)) return true;
            return startRow + countRow <= range.startRowIndex || range.startRowIndex + range.rowSpan <= startRow;
        };

        this.transaction(() => {
            // 1. 将删除范围内已合并的单元格进行解组
            this.forEachInTransaction(mergedRanges, (range) => {
                if (!isOutOfDeleteRange(range, startRow, countRow)) {
                    this.cancelMergedCell({
                        startRowIndex: range.startRowIndex,
                        startColIndex: range.startColIndex,
                        rowSpan: range.rowSpan,
                        colSpan: range.colSpan,
                    });
                }
            });

            this.forEachInTransaction(deleteRows as ICTTableRow[], (row, rowIndex) => {
                this.forEachInTransaction(row.tc as ICTTableCell[], (cell, colIndex) => {
                    // 设置单元格属性
                    if (cell?.tcPr) {
                        this.updateTableCellProperties(
                            {
                                noFill: true,
                            },
                            {
                                startRowIndex: startRow + rowIndex,
                                startColIndex: colIndex,
                                rowSpan: 1,
                                colSpan: 1,
                            },
                        );
                    }
                    // 设置单元格文本属性
                    if (cell?.txBody?.bodyPr || cell?.txBody?.lstStyle) {
                        this.updateTextBodyProperties({
                            col: colIndex,
                            row: startRow + rowIndex,
                        });
                    }
                    // 将删除范围内的单元格文本置空
                    if (cell.txBody?.p && cellHasParagraphText(cell.txBody.p)) {
                        this.updateParagraphList(
                            [
                                {
                                    egTextRun: [],
                                    endParaRPr: {},
                                    pPr: {},
                                },
                            ],
                            colIndex,
                            startRow + rowIndex,
                        );
                    }
                });
            });

            // 2. 删除行
            this.deleteTableRowsInternal(startRow, countRow);

            // 3. 设置纵向合并单元格
            this.forEachInTransaction(mergedRanges, (range) => {
                if (!isOutOfDeleteRange(range, startRow, countRow) && range.rowSpan - countRow > 0) {
                    if (range.rowSpan - countRow === 1 && range.colSpan === 1) {
                        return;
                    }
                    this.mergeCellInternal({
                        startRowIndex: range.startRowIndex,
                        startColIndex: range.startColIndex,
                        rowSpan: range.rowSpan - countRow,
                        colSpan: range.colSpan,
                    });
                }
            });
        });
    }

    private deleteTableRowsInternal(startRow: number, countRow: number) {
        this.transaction(() => {
            this.request([
                new DeleteTableRowMutation({
                    pageId: this.model.getPage().id,
                    shapeId: this.model.id,
                    index: startRow,
                    count: countRow,
                }),
            ]);
        });
    }

    /**
     * 删除表格列
     */
    private deleteTableCols(startCol: number, countCol: number) {
        const tableData = cloneDeep(this.model.getOwnedData().graphic?.graphicData?.tbl) as ICTTableDmlA;
        if (!tableData) return;

        const deleteCols = tableData?.tr?.map(
            (row) => row?.tc?.slice(startCol, startCol + countCol) || [],
        ) as ICTTableRow[];
        const mergedRanges = tableMergedRanges(tableData as ICTTableDmlA);
        const isOutOfDeleteRange = (range: Required<ITableRangeData>, startCol: number, countCol: number) => {
            if (!isValidTableRange(range)) return true;
            return startCol + countCol <= range.startColIndex || range.startColIndex + range.colSpan <= startCol;
        };

        this.transaction(() => {
            // 1. 将删除范围内已合并的单元格进行解组
            this.forEachInTransaction(mergedRanges, (range) => {
                if (!isOutOfDeleteRange(range, startCol, countCol)) {
                    this.cancelMergedCell({
                        startRowIndex: range.startRowIndex,
                        startColIndex: range.startColIndex,
                        rowSpan: range.rowSpan,
                        colSpan: range.colSpan,
                    });
                }
            });

            this.forEachInTransaction(deleteCols, (row, rowIndex) => {
                this.forEachInTransaction(row as ICTTableCell[], (cell, colIndex) => {
                    // 设置单元格属性
                    if (cell?.tcPr) {
                        this.updateTableCellProperties(
                            {
                                noFill: true,
                            },
                            {
                                startRowIndex: rowIndex,
                                startColIndex: startCol + colIndex,
                                rowSpan: 1,
                                colSpan: 1,
                            },
                        );
                    }
                    // 设置单元格文本属性
                    if (cell?.txBody?.bodyPr || cell?.txBody?.lstStyle) {
                        this.updateTextBodyProperties({
                            col: startCol + colIndex,
                            row: rowIndex,
                        });
                    }
                    // 将删除范围内的单元格文本置空
                    if (cell.txBody?.p && cellHasParagraphText(cell.txBody.p)) {
                        this.updateParagraphList(
                            [
                                {
                                    egTextRun: [],
                                    endParaRPr: {},
                                    pPr: {},
                                },
                            ],
                            startCol + colIndex,
                            rowIndex,
                        );
                    }
                });
            });

            // 2. 删除列
            this.deleteTableColsInternal(startCol, countCol);

            // 3. 设置横向合并单元格
            this.forEachInTransaction(mergedRanges, (range) => {
                if (!isOutOfDeleteRange(range, startCol, countCol) && range.colSpan - countCol > 0) {
                    if (range.colSpan - countCol === 1 && range.rowSpan === 1) {
                        return;
                    }
                    this.mergeCellInternal({
                        startRowIndex: range.startRowIndex,
                        startColIndex: range.startColIndex,
                        rowSpan: range.rowSpan,
                        colSpan: range.colSpan - countCol,
                    });
                }
            });
        });
    }

    private deleteTableColsInternal(startCol: number, countCol: number) {
        this.transaction(() => {
            this.request([
                new DeleteTableColMutation({
                    pageId: this.model.getPage().id,
                    shapeId: this.model.id,
                    index: startCol,
                    count: countCol,
                }),
            ]);
        });
    }

    /**
     * [合并单元格] 判断选中的范围是否可以合并，只有当选中范围中有合并单元格且合并范围超出选中范围时为 false
     * @param range 单元格范围
     */
    private canMergeCell(range: ITableRangeData) {
        const { startRowIndex, startColIndex, rowSpan, colSpan } = range;

        if (isNil(startRowIndex) || !rowSpan || isNil(startColIndex) || !colSpan) return false;
        if (Math.min(rowSpan, colSpan) <= 0 || Math.max(rowSpan, colSpan) <= 1) return false;

        const rowList = this.model.getOwnedData().graphic?.graphicData?.tbl?.tr as ICTTableRow[];
        const colList = this.model.getOwnedData().graphic?.graphicData?.tbl?.tblGrid?.gridCol as ICTTableCol[];
        if (rowSpan + startRowIndex > rowList.length) return false;
        if (colSpan + startColIndex > colList.length) return false;
        if (this.isFullMergedCell(range)) return false;
        return true;
        // let subTable: ICTTableRow[] = this.rowList.slice(startRowIndex, startRowIndex + rowSpan);
        // subTable = subTable.map((row: ICTTableRow) => ({
        //     h: row.h,
        //     tc: (row?.tc && row.tc.slice(startColIndex, startColIndex + colSpan)) || [],
        // }));

        // let result = true;
        // let hasInvalidCell = false;
        // subTable.some(
        //     (row: ICTTableRow, rIndex: number) =>
        //         row?.tc &&
        //         row.tc.some((cell: ICTTableCell, cIndex: number) => {
        //             if (hasRowSpan(cell) && cell.rowSpan! + rIndex > subTable.length) {
        //                 hasInvalidCell = true;
        //             } else if (hasGridSpan(cell) && cell.gridSpan! + cIndex > (row?.tc?.length || 0)) {
        //                 hasInvalidCell = true;
        //             } else if (cell.vMerge || cell.hMerge) {
        //                 const point = this.findFirstCellByMergedCell(rIndex + startRowIndex, cIndex + startColIndex);
        //                 if (!point || !this.isPointInRange(point, range)) {
        //                     hasInvalidCell = true;
        //                 }
        //             }
        //             if (hasInvalidCell) {
        //                 result = false;
        //             }
        //             return hasInvalidCell;
        //         }),
        // );
        // return result;
    }

    /* eslint-disable-next-line complexity */
    private isFullMergedCell(range: ITableRangeData) {
        const { startRowIndex, startColIndex, rowSpan, colSpan } = range;
        if (!startColIndex || !startRowIndex) return false;

        const rowList = this.model.getOwnedData().graphic?.graphicData?.tbl?.tr as ICTTableRow[];
        const firstCell = rowList[startRowIndex]?.tc?.[startColIndex];
        if (!firstCell) return;
        let result = false;

        if (hasRowSpan(firstCell) && hasGridSpan(firstCell)) {
            result = firstCell.rowSpan === rowSpan && firstCell.gridSpan === colSpan;
        } else if (hasRowSpan(firstCell)) {
            result = firstCell.rowSpan === rowSpan && colSpan === 1;
        } else if (hasGridSpan(firstCell)) {
            result = firstCell.gridSpan === colSpan && rowSpan === 1;
        }
        return result;
    }
}
